import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface Comentario {
  id: number;
  comentario_id: string;
  media_permalink: string | null;
  autor_username: string | null;
  texto: string;
  categoria: string;
  motivo: string | null;
  resposta_ia: string | null;
  resposta_id: string | null;
  status: string;
  erro: string | null;
  created_at: string;
  respondido_em: string | null;
}

interface Config {
  ativo: boolean;
  ocultar_ofensivos: boolean;
  tom: string | null;
}

interface Stats {
  total: number;
  porStatus: Record<string, number>;
  aguardando_humano: number;
}

const STATUS_FILTROS = [
  { key: 'respondido', label: 'Respondidos (IA)' },
  { key: 'agendado', label: 'Agendados' },
  { key: 'aguardando_humano', label: 'Precisa de humano' },
  { key: '', label: 'Todos' },
  { key: 'ignorado', label: 'Ignorados' },
  { key: 'oculto', label: 'Ocultados' },
  { key: 'resolvido', label: 'Resolvidos' },
  { key: 'erro', label: 'Com erro' },
];

const ROTULO_STATUS: Record<string, string> = {
  respondido: 'Respondido pela IA',
  agendado: 'Aguardando (delay)',
  aguardando_humano: 'Aguardando humano',
  ignorado: 'Ignorado',
  oculto: 'Ocultado',
  resolvido: 'Resolvido',
  erro: 'Erro',
  resposta_apagada: 'Resposta apagada',
};

const SocialComentarios: React.FC = () => {
  const [comentarios, setComentarios] = useState<Comentario[]>([]);
  const [config, setConfig] = useState<Config>({ ativo: false, ocultar_ofensivos: false, tom: '' });
  const [stats, setStats] = useState<Stats | null>(null);
  const [statusFiltro, setStatusFiltro] = useState<string>('respondido');
  const [respostas, setRespostas] = useState<Record<number, string>>({});
  const [processando, setProcessando] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [salvandoCfg, setSalvandoCfg] = useState<boolean>(false);

  // base de conhecimento
  const [kbAberta, setKbAberta] = useState<boolean>(false);
  const [kb, setKb] = useState<string>('');
  const [salvandoKb, setSalvandoKb] = useState<boolean>(false);

  useEffect(() => { fetchComentarios(statusFiltro); /* eslint-disable-next-line */ }, [statusFiltro]);
  useEffect(() => { fetchConfig(); fetchStats(); }, []);

  const fetchComentarios = async (status: string) => {
    setLoading(true); setError(null);
    try {
      const q = status ? `?status=${status}` : '';
      const r = await api.get(`/admin/social/comentarios${q}`);
      setComentarios(r.data);
    } catch (err) {
      console.error(err); setError('Erro ao carregar comentários.');
    } finally { setLoading(false); }
  };

  const fetchConfig = async () => {
    try {
      const r = await api.get('/admin/social/comentarios/config');
      setConfig({ ativo: !!r.data.ativo, ocultar_ofensivos: !!r.data.ocultar_ofensivos, tom: r.data.tom || '' });
    } catch (err) { console.error(err); }
  };

  const fetchStats = async () => {
    try { const r = await api.get('/admin/social/comentarios/stats'); setStats(r.data); }
    catch (err) { console.error(err); }
  };

  const salvarConfig = async (patch: Partial<Config>) => {
    const novo = { ...config, ...patch };
    setConfig(novo); setSalvandoCfg(true);
    try { await api.put('/admin/social/comentarios/config', patch); }
    catch (err) { console.error(err); setError('Erro ao salvar configuração.'); fetchConfig(); }
    finally { setSalvandoCfg(false); }
  };

  const responder = async (id: number) => {
    const mensagem = (respostas[id] || '').trim();
    if (!mensagem) return;
    setProcessando(id); setError(null);
    try {
      await api.put(`/admin/social/comentarios/${id}/responder`, { mensagem });
      await Promise.all([fetchComentarios(statusFiltro), fetchStats()]);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Falha ao responder.');
    } finally { setProcessando(null); }
  };

  const acao = async (id: number, fn: () => Promise<any>) => {
    setProcessando(id); setError(null);
    try { await fn(); await Promise.all([fetchComentarios(statusFiltro), fetchStats()]); }
    catch (err: any) { setError(err?.response?.data?.error || 'Falha na operação.'); }
    finally { setProcessando(null); }
  };

  const resolver = (id: number) => acao(id, () => api.put(`/admin/social/comentarios/${id}/resolver`));
  const ocultar = (id: number) => acao(id, () => api.put(`/admin/social/comentarios/${id}/ocultar`, { ocultar: true }));
  const apagarResposta = (id: number) => acao(id, () => api.delete(`/admin/social/comentarios/${id}/resposta`));

  const abrirKb = async () => {
    if (!kbAberta && !kb) {
      try { const r = await api.get('/admin/social/comentarios/kb'); setKb(r.data.conteudo || ''); }
      catch (err) { console.error(err); }
    }
    setKbAberta(!kbAberta);
  };

  const salvarKb = async () => {
    setSalvandoKb(true); setError(null);
    try { await api.put('/admin/social/comentarios/kb', { conteudo: kb }); }
    catch (err: any) { setError(err?.response?.data?.error || 'Erro ao salvar a base de conhecimento.'); }
    finally { setSalvandoKb(false); }
  };

  return (
    <div>
      {/* Barra de configuração / kill switch */}
      <div style={styles.painel}>
        <div style={styles.linhaCfg}>
          <label style={styles.switchLabel}>
            <input
              type="checkbox"
              checked={config.ativo}
              disabled={salvandoCfg}
              onChange={(e) => salvarConfig({ ativo: e.target.checked })}
            />
            <strong style={{ color: config.ativo ? '#2ecc71' : '#e74c3c' }}>
              {config.ativo ? 'IA LIGADA — respondendo comentários' : 'IA DESLIGADA — não responde nada'}
            </strong>
          </label>
          <label style={styles.switchLabel}>
            <input
              type="checkbox"
              checked={config.ocultar_ofensivos}
              disabled={salvandoCfg}
              onChange={(e) => salvarConfig({ ocultar_ofensivos: e.target.checked })}
            />
            Ocultar comentários ofensivos/spam automaticamente
          </label>
        </div>
        <div style={{ marginTop: 10 }}>
          <div style={styles.cfgTitulo}>Ajuste de tom (opcional)</div>
          <textarea
            style={styles.textareaTom}
            placeholder="Ex: pode ser mais informal, usar gírias da cena rock..."
            value={config.tom || ''}
            onChange={(e) => setConfig({ ...config, tom: e.target.value })}
            onBlur={() => salvarConfig({ tom: config.tom || '' })}
          />
        </div>
        <button style={styles.linkBtn} onClick={abrirKb}>
          {kbAberta ? '▲ Fechar base de conhecimento' : '▼ Editar base de conhecimento da IA'}
        </button>
        {kbAberta && (
          <div style={{ marginTop: 8 }}>
            <textarea style={styles.textareaKb} value={kb} onChange={(e) => setKb(e.target.value)} />
            <button style={styles.btnPrimario} disabled={salvandoKb} onClick={salvarKb}>
              {salvandoKb ? 'Salvando...' : 'Salvar base de conhecimento'}
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div style={styles.stats}>
          <span>Total: <strong>{stats.total}</strong></span>
          <span style={{ color: '#f39c12' }}>Precisam de humano: <strong>{stats.aguardando_humano}</strong></span>
          <span>Respondidos: <strong>{stats.porStatus['respondido'] || 0}</strong></span>
          <span>Ignorados: <strong>{stats.porStatus['ignorado'] || 0}</strong></span>
        </div>
      )}

      {/* Filtros */}
      <div style={styles.filtros}>
        {STATUS_FILTROS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatusFiltro(f.key)}
            style={{ ...styles.filtroBtn, ...(statusFiltro === f.key ? styles.filtroAtivo : {}) }}
          >
            {f.label}
            {f.key === 'aguardando_humano' && stats && stats.aguardando_humano > 0
              ? ` (${stats.aguardando_humano})` : ''}
          </button>
        ))}
      </div>

      {error && <div style={styles.erro}>{error}</div>}
      {loading && <div style={styles.info}>Carregando...</div>}
      {!loading && comentarios.length === 0 && <div style={styles.info}>Nenhum comentário aqui.</div>}

      {/* Lista */}
      {comentarios.map((c) => (
        <div key={c.id} style={styles.card}>
          <div style={styles.cardTopo}>
            <strong style={{ color: '#ff6600' }}>@{c.autor_username || 'desconhecido'}</strong>
            <span style={styles.badgeStatus}>{ROTULO_STATUS[c.status] || c.status}</span>
            <span style={styles.data}>{new Date(c.created_at).toLocaleString('pt-BR')}</span>
            {c.media_permalink && (
              <a href={c.media_permalink} target="_blank" rel="noreferrer" style={styles.link}>ver post ↗</a>
            )}
          </div>

          <div style={styles.comentario}>"{c.texto}"</div>
          {c.motivo && <div style={styles.motivo}>IA: {c.motivo}</div>}
          {c.resposta_ia && (
            <div style={styles.resposta}><strong>Resposta:</strong> {c.resposta_ia}</div>
          )}
          {c.erro && <div style={styles.erroInline}>Erro: {c.erro}</div>}

          {/* Ações para escalados / sem resposta */}
          {(c.status === 'aguardando_humano' || c.status === 'erro') && (
            <div style={{ marginTop: 10 }}>
              <textarea
                style={styles.textareaResp}
                placeholder="Escreva uma resposta para postar no Instagram..."
                value={respostas[c.id] || ''}
                onChange={(e) => setRespostas({ ...respostas, [c.id]: e.target.value })}
              />
              <div style={styles.botoes}>
                <button style={styles.btnPrimario} disabled={processando === c.id} onClick={() => responder(c.id)}>
                  {processando === c.id ? '...' : 'Responder no IG'}
                </button>
                <button style={styles.btnSecundario} disabled={processando === c.id} onClick={() => resolver(c.id)}>
                  Marcar resolvido
                </button>
                <button style={styles.btnPerigo} disabled={processando === c.id} onClick={() => ocultar(c.id)}>
                  Ocultar comentário
                </button>
              </div>
            </div>
          )}

          {/* Ações para respondidos pela IA */}
          {c.status === 'respondido' && c.resposta_id && (
            <div style={styles.botoes}>
              <button style={styles.btnPerigo} disabled={processando === c.id} onClick={() => apagarResposta(c.id)}>
                Apagar resposta da IA
              </button>
              <button style={styles.btnSecundario} disabled={processando === c.id} onClick={() => ocultar(c.id)}>
                Ocultar comentário
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  painel: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 16, marginBottom: 16 },
  linhaCfg: { display: 'flex', gap: 24, flexWrap: 'wrap' },
  switchLabel: { display: 'flex', alignItems: 'center', gap: 8, color: '#ddd', fontSize: 14, cursor: 'pointer' },
  cfgTitulo: { color: '#aaa', fontSize: 13, marginBottom: 4 },
  textareaTom: { width: '100%', minHeight: 44, background: '#1a1a1a', color: '#eee', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: 8, fontSize: 13, resize: 'vertical' },
  textareaKb: { width: '100%', minHeight: 280, background: '#1a1a1a', color: '#eee', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: 8, fontSize: 12, fontFamily: 'monospace', resize: 'vertical' },
  linkBtn: { background: 'transparent', border: 'none', color: '#ff6600', cursor: 'pointer', fontSize: 13, marginTop: 10, padding: 0 },
  stats: { display: 'flex', gap: 20, flexWrap: 'wrap', color: '#ccc', fontSize: 14, marginBottom: 14 },
  filtros: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  filtroBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: '#bbb', padding: '6px 14px', borderRadius: 16, cursor: 'pointer', fontSize: 13 },
  filtroAtivo: { background: '#ff6600', borderColor: '#ff6600', color: '#fff', fontWeight: 600 },
  card: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 14, marginBottom: 12 },
  cardTopo: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 },
  badgeStatus: { background: 'rgba(255,102,0,0.15)', color: '#ff8c33', padding: '2px 10px', borderRadius: 12, fontSize: 12 },
  data: { color: '#888', fontSize: 12 },
  link: { color: '#5dade2', fontSize: 12, textDecoration: 'none' },
  comentario: { color: '#eee', fontSize: 15, fontStyle: 'italic', marginBottom: 6 },
  motivo: { color: '#999', fontSize: 12, marginBottom: 4 },
  resposta: { color: '#2ecc71', fontSize: 14, background: 'rgba(46,204,113,0.08)', padding: 8, borderRadius: 6, marginTop: 4 },
  erroInline: { color: '#e74c3c', fontSize: 13, marginTop: 4 },
  textareaResp: { width: '100%', minHeight: 56, background: '#1a1a1a', color: '#eee', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: 8, fontSize: 14, resize: 'vertical' },
  botoes: { display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' },
  btnPrimario: { background: '#ff6600', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  btnSecundario: { background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: '#ccc', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  btnPerigo: { background: 'transparent', border: '1px solid #e74c3c', color: '#e74c3c', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  erro: { background: 'rgba(231,76,60,0.12)', color: '#e74c3c', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 14 },
  info: { color: '#999', fontSize: 14, padding: 12 },
};

export default SocialComentarios;
